import { QueryClient } from "@tanstack/react-query";
import { toPillarApiError } from "shared/api/PillarApiError";

/** React Query's own default, kept for everything the 4xx rule does not cover. */
const DEFAULT_RETRY_COUNT = 3;

/**
 * The retry policy, set as a QueryClient default so any single query can still
 * override it.
 *
 * A 4xx means the request itself was wrong - a filter the API rejects, a record
 * this caller may not read - and will fail the same way three more times, so
 * retrying only delays showing the reason. Anything else (a 5xx, a dropped
 * connection) may well be a blip worth another try.
 *
 * Exported for the native client, which replaces the query defaults wholesale
 * and would otherwise drop this.
 */
export const shouldRetryPillarQuery = (
  failureCount: number,
  error: unknown,
): boolean => {
  const { statusCode } = toPillarApiError(error);
  if (statusCode >= 400 && statusCode <= 499) {
    return false;
  }
  return failureCount < DEFAULT_RETRY_COUNT;
};

/**
 * The one QueryClient both admin and the hub run on.
 *
 * Each app bundles its own copy, so this is a per-app singleton rather than
 * anything actually shared between them - what is shared is the configuration.
 */
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: shouldRetryPillarQuery } },
});

export { queryClient };
